import { Component, inject, OnDestroy, signal } from '@angular/core';
import { CommonModule, JsonPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AuthenticationStore } from '@argo/authentication';
import { WebsocketStore } from '@argo/messaging';
import { 
  ScaModuleTitleComponent,
  ScaCardWorkshopComponent,
  ScaAlertComponent,
  ScaBadgeComponent,
  ScaIconComponent,
  ScaButtonComponent
} from '@argo-dev-components-sca-angular/dev-components-sca';

import { toObservable } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-websocket-tester',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    JsonPipe,
    ScaModuleTitleComponent,
    ScaCardWorkshopComponent,
    ScaAlertComponent,
    ScaBadgeComponent,
    ScaIconComponent,
    ScaButtonComponent
  ],
  template: `
    <div class="container">
      <sca-module-title class="title-test">
    <h1>Websocket Playground</h1>
    <span scaModuleTitlePost>Librería @argo/messaging - Gestión de WebSocket con protocolo STOMP - 100% cobertura</span>
  </sca-module-title>

      <!-- Estado -->
      <section class="card">
        <h2>Estado de Conexión</h2>
        
        <sca-alert variant="info" style="margin-bottom: 1.5rem;">
          <sca-icon icon="info" size="24px" [weight]="300" left />
          Estado actual de la conexión WebSocket/STOMP
        </sca-alert>
        
        <div class="utilities-grid">
          <sca-card-workshop [selected]="authService.logged()">
            <header scaCardWorkshopHeader>
              <sca-icon icon="login" size="24" [weight]="400" />
            </header>
            <p scaCardWorkshopTitle>Estado de Login</p>
            <p scaCardWorkshopAddress>
              <sca-badge [variant]="authService.logged() ? 'success' : 'error'">
                {{ authService.logged() ? 'LOGGED IN' : 'LOGGED OUT' }}
              </sca-badge>
            </p>
          </sca-card-workshop>

          <sca-card-workshop [selected]="websocketService.status() === 'CONNECTED'">
            <header scaCardWorkshopHeader>
              <sca-icon icon="cable" size="24" [weight]="400" />
            </header>
            <p scaCardWorkshopTitle>Estado WebSocket</p>
            <p scaCardWorkshopAddress>
              <sca-badge 
                [variant]="websocketService.status() === 'CONNECTED' ? 'success' : 
                          websocketService.status() === 'CONNECTING' ? 'accent' : 'error'">
                {{ websocketService.status() }}
              </sca-badge>
            </p>
          </sca-card-workshop>

          <sca-card-workshop>
            <header scaCardWorkshopHeader>
              <sca-icon icon="message" size="24" [weight]="400" />
            </header>
            <p scaCardWorkshopTitle>Mensajes Recibidos</p>
            <p scaCardWorkshopAddress>
              <sca-badge variant="default">{{ websocketService.receivedMessages().length }}</sca-badge>
            </p>
          </sca-card-workshop>
        </div>
      </section>

      <!-- Suscripciones -->
      <section class="card">
        <h2>Suscripciones a Tópicos</h2>
        <div class="form-group">
          <label for="topic">Tópico STOMP:</label>
          <input id="topic" type="text" [(ngModel)]="topicToHandle" placeholder="/topic/greetings">
          <div class="button-group">
            <button 
              sca-button
              variant="primary" 
              size="small" 
              (click)="subscribe()" 
              [disabled]="!topicToHandle">
              Suscribirse
            </button>
            <button 
              sca-button
              variant="border" 
              size="small" 
              (click)="unsubscribe()" 
              [disabled]="!topicToHandle">
              Desuscribirse
            </button>
          </div>
        </div>
      </section>

      <!-- Publicar Mensaje -->
      <section class="card">
        <h2>Publicar Mensaje</h2>
        <div class="form-group">
          <label for="destination">Destino:</label>
          <input id="destination" type="text" [(ngModel)]="messageDestination" placeholder="/app/hello">
          
          <label for="body">Cuerpo (JSON):</label>
          <textarea id="body" [(ngModel)]="messageBody" placeholder='{"name": "Angular"}'></textarea>

          <button 
            sca-button
            variant="primary" 
            size="small" 
            (click)="publishMessage()" 
            [disabled]="!messageDestination || !messageBody">
            Publicar
          </button>

          @if (publishError()) {
            <sca-alert variant="error">
              {{ publishError() }}
            </sca-alert>
          }
        </div>
      </section>

      <!-- Mensajes Recibidos -->
      <section class="card">
        <h2>Mensajes Recibidos</h2>
        <div class="message-box">
          @for (msg of websocketService.receivedMessages(); track msg.rawMessage.headers['message-id']) {
            <div class="message">
              <span class="topic">Topic: {{ msg.topic }}</span>
              <pre>{{ msg.payload | json }}</pre>
            </div>
          } @empty {
            <p class="empty-state">Aún no se han recibido mensajes.</p>
          }
        </div>
      </section>

      <!-- Información STOMP -->
      <section class="card">
        <h2>Protocolo STOMP</h2>
        
        <sca-alert variant="info" style="margin-bottom: 1.5rem;">
          <sca-icon icon="info" size="24px" [weight]="300" left />
          Características del protocolo STOMP sobre WebSocket
        </sca-alert>
        
        <div class="utilities-grid">
          <sca-card-workshop>
            <header scaCardWorkshopHeader>
              <sca-icon icon="settings_ethernet" size="24" [weight]="400" />
            </header>
            <p scaCardWorkshopTitle>Conexión Automática</p>
            <p scaCardWorkshopAddress>Se conecta automáticamente al hacer login</p>
          </sca-card-workshop>

          <sca-card-workshop>
            <header scaCardWorkshopHeader>
              <sca-icon icon="power_off" size="24" [weight]="400" />
            </header>
            <p scaCardWorkshopTitle>Desconexión Automática</p>
            <p scaCardWorkshopAddress>Se desconecta al hacer logout</p>
          </sca-card-workshop>

          <sca-card-workshop>
            <header scaCardWorkshopHeader>
              <sca-icon icon="topic" size="24" [weight]="400" />
            </header>
            <p scaCardWorkshopTitle>Patrón Pub/Sub</p>
            <p scaCardWorkshopAddress>Suscripción a tópicos y publicación de mensajes</p>
          </sca-card-workshop>
        </div>
      </section>
    </div>
  `,
  styles: [`
    .container { padding: 0 2rem; margin: 0 auto;     @media (max-width: 1400px) {
        padding-left: 0 !important;
        padding-right: 0 !important;
    }}
    .title-test{ margin-bottom: 2rem; }
    .card { background: white; border-radius: 8px; padding: 2rem; margin-bottom: 2rem; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
    .card h2 { color: #333; margin-bottom: 1.5rem; padding-bottom: 0.5rem;  }
    .utilities-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 1.5rem; margin: 1.5rem 0; }
    .form-group { display: flex; flex-direction: column; gap: 0.75rem; }
    label { font-weight: 600; font-size: 0.9rem; color: #333; }
    input, textarea { width: 100%; padding: 0.75rem; border-radius: 6px; border: 1px solid #ddd; box-sizing: border-box; font-family: inherit; }
    textarea { height: 100px; resize: vertical; font-family: 'Courier New', monospace; }
    .button-group { display: flex; gap: 0.75rem; }
    .message-box { background: #263238; color: #aed581; border: 1px solid #444; height: 400px; overflow-y: auto; padding: 1rem; border-radius: 8px; margin-top: 1rem; }
    .message { border-bottom: 1px solid #37474f; padding-bottom: 0.75rem; margin-bottom: 0.75rem; }
    .topic { font-weight: 600; font-size: 0.85rem; color: #80cbc4; display: block; margin-bottom: 0.5rem; }
    pre { white-space: pre-wrap; word-wrap: break-word; margin: 0; background: #1e272c; padding: 0.75rem; border-radius: 6px; }
    .empty-state { color: #78909c; text-align: center; padding-top: 5rem; font-style: italic; }
  `]
})
export class WebsocketTesterComponent implements OnDestroy {
  public websocketService = inject(WebsocketStore);
  public authService = inject(AuthenticationStore);

  private destroy$ = new Subject<void>();

  public topicToHandle: string = '/topic/greetings';
  public messageDestination: string = '/app/hello';
  public messageBody: string = '{"name": "Angular"}';

  public publishError = signal<string | null>(null);

  constructor() {
    toObservable(this.authService.logged)
      .pipe(
        filter(isLoggedIn => isLoggedIn === true),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        console.log('Usuario logueado, conectando WebSocket...');
        this.websocketService.connect();
      });

    toObservable(this.authService.logged)
      .pipe(
        filter(isLoggedIn => isLoggedIn === false),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        console.log('Usuario deslogueado, desconectando WebSocket...');
        this.websocketService.disconnect();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  subscribe(): void {
    if (this.topicToHandle) {
      this.websocketService.subscribeToTopic(this.topicToHandle);
    }
  }

  unsubscribe(): void {
    if (this.topicToHandle) {
      this.websocketService.unsubscribeFromTopic(this.topicToHandle);
    }
  }

  publishMessage(): void {
    this.publishError.set(null);

    if (!this.messageDestination || !this.messageBody) {
      this.publishError.set('El destino y el cuerpo del mensaje son obligatorios.');
      return;
    }
    try {
      const body = JSON.parse(this.messageBody);
      this.websocketService.publish(this.messageDestination, body);
    } catch (e) {
      this.publishError.set('El cuerpo del mensaje no es un objeto JSON válido.');
      console.error(e);
    }
  }
}


